import { FilterChangedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import { IFloatingFilter, IFloatingFilterParams } from '@ag-grid-community/core/dist/esm/es6/filter/floating/floatingFilter';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Core, easepick } from '@easepick/core';
import { KbdPlugin } from '@easepick/kbd-plugin';
import { RangePlugin } from '@easepick/range-plugin';
import { DATEPICKER_STYLES } from 'shared/constants/general-constants';

@Component({
    selector: 'app-date-range-floating-filter',
    templateUrl: './date-range-floating-filter.component.html',
    styleUrls: ['./date-range-floating-filter.component.scss'],
    standalone: false,
})
export class DateRangeFloatingFilterComponent implements IFloatingFilter, AfterViewInit, OnDestroy {
    @ViewChild('dateInput', { read: ElementRef }) dateInput!: ElementRef;

    public dateRangeControl = new FormControl<string | null>(null);
    public format: string = 'YYYY-MM-DD';
    public hostElement!: HTMLElement;
    public startDateField: string = 'startDateTimestamp';
    public endDateField: string = 'endDateTimestamp';

    private params!: IFloatingFilterParams;
    private datepicker!: Core;

    constructor(
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
    ) {}

    public agInit(params: IFloatingFilterParams): void {
        this.params = params;
        this.startDateField = this.params.column.getColDef().filterParams?.startDateField || this.startDateField;
        this.endDateField = this.params.column.getColDef().filterParams?.endDateField || this.endDateField;
    }

    public ngAfterViewInit(): void {
        this.format = this.params.column.getColDef().filterParams?.format || this.format;
        this.hostElement = document.createElement('div');
        this.hostElement.classList.add(`calendar-picker-${(this.params as any).column.colId}-host`);
        document.body.querySelector('.ag-root-wrapper')?.appendChild(this.hostElement);
        this.hostElement.style.fontSize = '0';

        this.zone.runOutsideAngular(() => {
            this.datepicker = new easepick.create({
                element: document.body.querySelector(`.calendar-picker-${(this.params as any).column.colId}-host`),
                readonly: false,
                firstDay: 1,
                format: this.format,
                plugins: [RangePlugin, KbdPlugin],
                css: ['https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css'],
                RangePlugin: {
                    tooltip: true,
                    delimiter: ' - ',
                },
            } as any);
        });

        this.restoreFilterState();

        this.params.api.addEventListener('filterChanged', () => {
            this.zone.run(() => {
                this.restoreFilterState();
            });
        });

        this.datepicker.on('select', (event: any) =>
            this.zone.run(
                () => {
                    const startDate = event.detail.start.format(this.format);
                    const endDate = event.detail.end.format(this.format);
                    const displayValue = `${startDate} - ${endDate}`;
                    const gridFilterModel = this.params.api.getFilterModel();

                    this.dateRangeControl.setValue(displayValue);

                    gridFilterModel[this.startDateField] = {
                        filterType: 'text',
                        type: 'equals',
                        filter: startDate,
                    };

                    gridFilterModel[this.endDateField] = {
                        filterType: 'text',
                        type: 'equals',
                        filter: endDate,
                    };

                    this.params.api.setFilterModel(gridFilterModel);
                },
                this,
                [event],
            ),
        );

        this.datepicker.ui.wrapper.classList.add(`calendar-picker-${(this.params as any).column.colId}-parasite`);

        this.dateInput.nativeElement.addEventListener(
            'keydown',
            (event: any) => {
                if (event.key === 'Escape' && this.datepicker.isShown()) {
                    event.preventDefault();
                    this.closeCalendar(event);
                }
            },
            { capture: true },
        );

        const easePickStyle = document.createElement('style');
        easePickStyle.innerHTML = DATEPICKER_STYLES;
        this.datepicker.ui.shadowRoot.append(easePickStyle);
    }

    private restoreFilterState(): void {
        const gridFilterModel = this.params.api.getFilterModel();
        const startDate = gridFilterModel[this.startDateField]?.filter;
        const endDate = gridFilterModel[this.endDateField]?.filter;

        if (startDate && endDate) {
            const displayValue = `${startDate} - ${endDate}`;
            this.dateRangeControl.setValue(displayValue);
            if (this.datepicker) {
                this.datepicker.setDateRange(startDate, endDate);
            }
        } else {
            this.dateRangeControl.setValue(null);
            if (this.datepicker) {
                this.datepicker.clear();
            }
        }
        this.cdr.detectChanges();
    }

    public closeCalendar(event: Event) {
        this.zone.runOutsideAngular(() => {
            if (this.datepicker.isShown()) {
                this.datepicker.hide();
                event.stopPropagation();
            }
        });
    }

    public openCalendar(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.positionCalendarBelowInput();
        this.datepicker.show();
    }

    private positionCalendarBelowInput() {
        const commonParentClass = 'ag-root-wrapper';
        const commonParent = document.querySelector(`.${commonParentClass}`);

        const { top, left } = getOffsetRelativeToParent(this.dateInput.nativeElement, commonParent);

        this.hostElement.style.position = 'absolute';
        this.hostElement.style.left = `${left}px`;
        this.hostElement.style.top = `${top + this.dateInput.nativeElement.offsetHeight}px`;
    }

    public clearDate(): void {
        this.datepicker.clear();
        this.datepicker.gotoDate(new Date());
        this.dateRangeControl.setValue(null);

        const gridFilterModel = this.params.api.getFilterModel();
        delete gridFilterModel[this.startDateField];
        delete gridFilterModel[this.endDateField];

        this.params.api.setFilterModel(gridFilterModel);
    }

    public ngOnDestroy() {
        this.zone.runOutsideAngular(() => {
            this.datepicker.destroy();
        });
    }

    public onParentModelChanged(parentModel: any, filterChangedEvent?: FilterChangedEvent | null): void {
        const gridFilterModel = this.params.api.getFilterModel();
        const startDate = gridFilterModel[this.startDateField]?.filter;
        const endDate = gridFilterModel[this.endDateField]?.filter;

        if (startDate && endDate) {
            const displayValue = `${startDate} - ${endDate}`;
            this.dateRangeControl.setValue(displayValue);
            this.datepicker.setDateRange(startDate, endDate);
        } else {
            this.dateRangeControl.setValue(null);
            this.datepicker.clear();
        }

        this.cdr.detectChanges();
    }
}

function getOffsetRelativeToParent(element: any, parent: any) {
    let top = 0;
    let left = 0;
    let currentElement = element;

    while (currentElement && currentElement !== parent) {
        top += currentElement.offsetTop;
        left += currentElement.offsetLeft;
        currentElement = currentElement.offsetParent;
    }

    return { top, left };
}
